import React, { useCallback, useEffect, useState } from 'react';
import PageLoader from '../components/PageLoader';
import { AppButton } from '../components/authenticated-ui/AuthenticatedUi';
import { useActor } from '../app/ActorContext';
import { apiJson } from '../services/api';
import './ContractorDirectory.css';

const EMPTY_FORM = { business_name: '', email: '', phone: '', relationship_status: 'prospect', trades: '', internal_notes: '' };
const STATUS_LABELS = { prospect: 'Prospect', active: 'Active', preferred: 'Preferred', paused: 'Paused', do_not_use: 'Do not use' };

export const tradeListFromText = (value) => String(value || '').split(',').map((item) => item.trim()).filter(Boolean);

export default function ContractorDirectory() {
  const { actor, hasPermission } = useActor();
  const [contractors, setContractors] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const loadContractors = useCallback(async (term = '') => {
    setLoading(true); setError('');
    try {
      const rows = await apiJson(`/contractors${term ? `?search=${encodeURIComponent(term)}` : ''}`);
      setContractors(Array.isArray(rows) ? rows : []);
    } catch (requestError) {
      setError(requestError.message || 'Could not load contractors.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadContractors(); }, [loadContractors]);

  const submit = async (event) => {
    event.preventDefault(); setSubmitting(true); setError('');
    try {
      await apiJson('/contractors', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...form, trades: tradeListFromText(form.trades) }),
      });
      setForm(EMPTY_FORM); setShowForm(false); await loadContractors(search);
    } catch (requestError) {
      setError(requestError.message || 'Could not create contractor.');
    } finally {
      setSubmitting(false);
    }
  };

  if (!actor?.active_organization_id) return <main className="contractor-directory"><h1>Contractors</h1><p>An active investor workspace is required before contractor records can be created or viewed.</p></main>;
  if (!hasPermission('contractor.read')) return <main className="contractor-directory"><h1>Contractors</h1><p>You do not have permission to view this contractor directory.</p></main>;

  return <main className="contractor-directory">
    <header className="contractor-directory__header">
      <div><p>CONTRACTOR CRM</p><h1>Contractors</h1><span>Track relationships, capabilities, work history, performance, and lessons.</span></div>
      {hasPermission('contractor.manage') && <AppButton variant="primary" label={showForm ? 'Close' : 'Add contractor'} icon={`pi ${showForm ? 'pi-times' : 'pi-plus'}`} onClick={() => setShowForm((value) => !value)} />}
    </header>

    {showForm && <form className="contractor-directory__form" onSubmit={submit}>
      <label>Business name<input value={form.business_name} onChange={(event) => setForm({ ...form, business_name: event.target.value })} required /></label>
      <label>Email<input type="email" value={form.email} onChange={(event) => setForm({ ...form, email: event.target.value })} /></label>
      <label>Phone<input value={form.phone} onChange={(event) => setForm({ ...form, phone: event.target.value })} /></label>
      <label>Relationship<select value={form.relationship_status} onChange={(event) => setForm({ ...form, relationship_status: event.target.value })}>{Object.entries(STATUS_LABELS).filter(([value]) => value !== 'do_not_use').map(([value, label]) => <option key={value} value={value}>{label}</option>)}</select></label>
      <label className="contractor-directory__wide">Trades <small>Separate with commas</small><input value={form.trades} onChange={(event) => setForm({ ...form, trades: event.target.value })} placeholder="General contracting, Plumbing, Electrical" /></label>
      <label className="contractor-directory__wide">Internal notes<textarea value={form.internal_notes} onChange={(event) => setForm({ ...form, internal_notes: event.target.value })} rows="3" /></label>
      <div className="contractor-directory__form-actions"><AppButton type="submit" variant="primary" label={submitting ? 'Saving…' : 'Save contractor'} disabled={submitting} /></div>
    </form>}

    <section className="contractor-directory__toolbar">
      <div><strong>{contractors.length}</strong><span>{contractors.length === 1 ? ' contractor' : ' contractors'}</span></div>
      <label><i className="pi pi-search" /><input aria-label="Search contractors" value={search} onChange={(event) => setSearch(event.target.value)} onKeyDown={(event) => { if (event.key === 'Enter') loadContractors(search); }} placeholder="Search contractors" /></label>
    </section>
    {error && <p className="contractor-directory__error" role="alert">{error}</p>}
    {loading ? <PageLoader compact label="Loading contractors" /> : contractors.length === 0 ? <section className="contractor-directory__empty"><i className="pi pi-users" /><h2>No contractors yet</h2><p>Add the first contractor to begin building your work and performance history.</p></section> : <section className="contractor-directory__list">
      {contractors.map((contractor) => <article key={contractor.contractor_id}>
        <div className="contractor-directory__identity"><span className="contractor-directory__avatar"><i className="pi pi-building" /></span><div><h2>{contractor.business_name}</h2><p>{contractor.email || contractor.phone || 'Contact details not added'}</p></div></div>
        <div className="contractor-directory__trades">{contractor.trades?.length ? contractor.trades.map((trade) => <span key={trade}>{trade}</span>) : <em>No trades recorded</em>}</div>
        <span className={`contractor-directory__status is-${contractor.relationship_status}`}>{STATUS_LABELS[contractor.relationship_status] || contractor.relationship_status}</span>
      </article>)}
    </section>}
  </main>;
}
